/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ChartItem, Info, ItemInfo } from '../../UNSAFE_Chart';
import {
  getNextItemInGroup,
  getNextItemInSeries,
  getPrevItemInGroup,
  getPrevItemInSeries
} from '../../utils/PRIVATE_chartUtils/navUtils';

type isItemNavigableType = (seriesIndex: number, groupIndex: number) => boolean;

/**
 * Get next item in unstacked bar chart.
 * @param info
 * @param viewportEndIndex
 * @param numSeries
 * @param isNavigable
 * @returns
 */
export function getNextItem(
  info: ItemInfo,
  viewportEndIndex: number,
  numSeries: number,
  isNavigable: isItemNavigableType
): ItemInfo {
  const nextItemInGroup = getNextItemInGroup(info, numSeries, isNavigable);
  if (nextItemInGroup) return nextItemInGroup;

  if (info.groupIndex < viewportEndIndex) {
    let nextItem = { seriesIndex: 0, groupIndex: info.groupIndex + 1 };
    if (isNavigable(nextItem.seriesIndex, nextItem.groupIndex)) return nextItem;
    nextItem = getNextItem(nextItem, viewportEndIndex, numSeries, isNavigable);
    if (nextItem) return nextItem;
  }
  return info;
}

/**
 * Get prev item in unstacked bar chart.
 * @param info
 * @param viewportStartIndex
 * @param numSeries
 * @param isNavigable
 * @returns
 */
export function getPreviousItem(
  info: ItemInfo,
  viewportStartIndex: number,
  numSeries: number,
  isNavigable: (seriesIndex: number, groupIndex: number) => boolean
) {
  const prevItemInGroup = getPrevItemInGroup(info, isNavigable);
  if (prevItemInGroup) return prevItemInGroup;

  if (info.groupIndex > viewportStartIndex) {
    let prevItem = { groupIndex: info.groupIndex - 1, seriesIndex: numSeries - 1 };
    if (isNavigable(prevItem.seriesIndex, prevItem.groupIndex)) return prevItem;
    prevItem = getPreviousItem(prevItem, viewportStartIndex, numSeries, isNavigable);
    if (prevItem) return prevItem;
  }
  return info;
}

export function getBarChartNavUtil<K, D extends ChartItem<K>>(
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  numSeries: number,
  viewportStartIndex: number,
  viewportEndIndex: number,
  isStacked: boolean,
  isRtl: boolean
) {
  const isItemNavigable = (seriesIndex: number, groupIndex: number) => {
    const barItem = getDataItem(seriesIndex, groupIndex);
    return Boolean(barItem);
  };

  const getNextChartItem = (
    itemInfo: Info,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
  ): Info => {
    if (!isStacked && (arrow === 'ArrowDown' || arrow === 'ArrowUp')) {
      return itemInfo;
    }
    let newInfo: Info | undefined;
    // TODO: this is just to keep typescript happy.
    // figure out consistent typing while doing type cleanup.
    const info = {
      groupIndex: itemInfo.groupIndex!,
      seriesIndex: itemInfo.seriesIndex!
    };

    if (info.groupIndex! < viewportStartIndex) {
      info.groupIndex = viewportStartIndex;
      return info;
    } else if (info.groupIndex! > viewportEndIndex) {
      info.groupIndex = viewportEndIndex;
      return info;
    }

    if (isStacked) {
      if (arrow === 'ArrowLeft') {
        newInfo = isRtl
          ? getNextItemInSeries(info, viewportEndIndex, isItemNavigable)
          : getPrevItemInSeries(info, viewportStartIndex, isItemNavigable);
      } else if (arrow === 'ArrowRight') {
        newInfo = isRtl
          ? getPrevItemInSeries(info, viewportStartIndex, isItemNavigable)
          : getNextItemInSeries(info, viewportEndIndex, isItemNavigable);
      } else if (arrow === 'ArrowDown') {
        newInfo = getPrevItemInGroup(info, isItemNavigable);
      } else {
        newInfo = getNextItemInGroup(info, numSeries, isItemNavigable);
      }
    } else {
      if (arrow === 'ArrowRight') {
        newInfo = isRtl
          ? getPreviousItem(info, viewportStartIndex, numSeries, isItemNavigable)
          : getNextItem(info, viewportEndIndex, numSeries, isItemNavigable);
      } else if (arrow === 'ArrowLeft') {
        newInfo = isRtl
          ? getNextItem(info, viewportEndIndex, numSeries, isItemNavigable)
          : getPreviousItem(info, viewportStartIndex, numSeries, isItemNavigable);
      }
    }
    return newInfo || info;
  };

  return getNextChartItem;
}
